import { Link } from 'react-router-dom';
import type { SourceReadModel } from '../../lib/source-read-model/read-model';

function formatTimestamp(date: Date | undefined): string {
  if (!date) return 'No sync recorded';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

function reauthAvailability(source: SourceReadModel): string {
  if (source.supportsReauthRoute) return 'Provider ReAuth available';
  if (source.id === 'location') return 'Virtual location uses local permission';
  return 'Local permission source';
}

export function SourceDetailPanel({ source }: { source: SourceReadModel }) {
  return (
    <aside
      data-testid="source-detail-aside"
      data-source-id={source.id}
      data-display-state={source.displayState}
      className="rounded-lg border border-neutral-200 bg-white/80 p-4 shadow-sm"
    >
      <p className="text-xs font-medium uppercase tracking-wide text-emerald-700">Source detail</p>
      <h2 className="mt-1 font-serif text-2xl font-semibold text-neutral-950">{source.label}</h2>

      <dl className="mt-4 grid gap-3 text-sm">
        <div>
          <dt className="text-xs uppercase tracking-wide text-neutral-500">State</dt>
          <dd className="font-medium text-neutral-900">{source.displayState}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-neutral-500">Provider</dt>
          <dd className="font-medium text-neutral-900">{source.provider}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-neutral-500">Last sync</dt>
          <dd className="font-medium text-neutral-900">{formatTimestamp(source.lastSync)}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-neutral-500">Signals</dt>
          <dd className="font-medium text-neutral-900">{source.itemCount ?? 0}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-neutral-500">Recovery</dt>
          <dd className="font-medium text-neutral-900">{reauthAvailability(source)}</dd>
        </div>
        {source.authOutcome?.reason && (
          <div>
            <dt className="text-xs uppercase tracking-wide text-neutral-500">Reason</dt>
            <dd className="font-medium text-neutral-900">{source.authOutcome.reason}</dd>
          </div>
        )}
        {source.error && (
          <div>
            <dt className="text-xs uppercase tracking-wide text-neutral-500">Problem</dt>
            <dd className="font-medium text-neutral-900">{source.error}</dd>
          </div>
        )}
      </dl>

      <div className="mt-5 grid gap-4 text-sm">
        <section>
          <h3 className="text-xs font-medium uppercase tracking-wide text-neutral-500">Permissions</h3>
          <ul data-testid="source-detail-permissions" className="mt-2 grid gap-1 text-neutral-700">
            {source.permissions.map(permission => (
              <li key={permission}>{permission}</li>
            ))}
          </ul>
        </section>
        <section>
          <h3 className="text-xs font-medium uppercase tracking-wide text-neutral-500">Recent activity</h3>
          <ul data-testid="source-detail-activity" className="mt-2 grid gap-1 text-neutral-700">
            {source.recentActivityLabels.map(label => (
              <li key={label}>{label}</li>
            ))}
          </ul>
        </section>
      </div>

      <div className="mt-5 flex flex-wrap gap-2 text-sm">
        <Link
          data-testid="source-detail-route-link"
          className="rounded-md border border-neutral-200 px-3 py-2 font-medium text-neutral-700 hover:bg-neutral-50"
          to={`/connections/${source.id}`}
        >
          Open detail
        </Link>
        {source.supportsReauthRoute && source.displayState === 'needs_reauth' && (
          <Link
            data-testid="source-reauth-route-link"
            className="rounded-md bg-emerald-700 px-3 py-2 font-medium text-white hover:bg-emerald-800"
            to={`/connections/${source.id}/reauth`}
          >
            Reconnect
          </Link>
        )}
      </div>
    </aside>
  );
}
